import { useState } from "react";
import { Repeat, ArrowRight, Settings } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatUsd } from "../lib/api";

const STATUS_LABEL = {
  active: "Active", trialing: "In Trial", past_due: "Past Due",
  canceled: "Cancelled", paused: "Paused", unpaid: "Unpaid", incomplete: "Incomplete",
};

const STATUS_STYLES = {
  active: "bg-[#C5CFB6] text-[#4F5F3F]",
  trialing: "bg-[#F0DFD0] text-[#2A1F1F]",
  past_due: "bg-[#F5E4D6] text-[#8E4038]",
  canceled: "bg-[#F0DFD0] text-[#6B5757]",
  paused: "bg-[#F5E4D6] text-[#8E4038]",
};

export default function SubscriptionsPage() {
  const [email, setEmail] = useState("");
  const [subs, setSubs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get("/subscriptions/mine", { params: { email } });
      setSubs(data);
    } catch { toast.error("Could not load subscriptions"); }
    finally { setLoading(false); }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await apiClient.post("/subscriptions/portal", {
        email,
        return_url: window.location.origin + "/account/subscriptions",
      });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "No active subscription for this email");
      setPortalLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-16 py-16">
      <div className="eyebrow">Your Account</div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight">
        Manage your <span className="serif-italic text-[#B85C56]">subscriptions.</span>
      </h1>
      <p className="text-[#6B5757] mt-6 max-w-lg leading-relaxed">
        Pause a delivery, change how often it arrives, or cancel anytime. Enter the email you used to subscribe.
      </p>

      <form onSubmit={lookup} className="mt-10 flex gap-3 max-w-md">
        <input
          data-testid="subs-email-input"
          type="email" required value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="flex-1 px-4 py-3 text-sm bg-white border border-[#E5CDBB] rounded-full focus:outline-none focus:border-[#2A1F1F]"
        />
        <button data-testid="subs-lookup" disabled={loading} className="btn-primary disabled:opacity-60">
          {loading ? "Loading…" : "Find"}
        </button>
      </form>

      {subs !== null && (
        <div className="mt-14">
          {subs.length === 0 ? (
            <div className="border border-dashed border-[#E5CDBB] rounded-lg p-16 text-center">
              <p className="font-serif text-2xl text-[#6B5757]">No subscriptions found</p>
              <p className="text-sm text-[#6B5757] mt-2">Try the email you used at checkout.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  data-testid="subs-portal-btn"
                  onClick={openPortal} disabled={portalLoading}
                  className="btn-outline inline-flex items-center gap-2 disabled:opacity-60"
                >
                  <Settings className="w-4 h-4" /> {portalLoading ? "Opening…" : "Manage in Stripe"}
                </button>
              </div>
              {subs.map((s) => (
                <div key={s.session_id} data-testid={`subs-item-${s.refill_id}`} className="soft-card p-6 flex flex-col md:flex-row gap-6 items-start md:items-center">
                  {s.image && <img src={s.image} alt="" className="w-20 h-20 object-cover rounded-md" />}
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#B85C56]">{s.brand}</div>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_STYLES[s.subscription_status] || "bg-[#F0DFD0] text-[#6B5757]"}`}>
                        {STATUS_LABEL[s.subscription_status] || s.subscription_status || "Pending"}
                      </span>
                    </div>
                    <div className="font-serif text-xl mt-2">{s.name}</div>
                    <div className="text-sm text-[#6B5757] mt-1 flex items-center gap-2">
                      <Repeat className="w-3 h-3" /> {formatUsd(s.unit_price)} every {s.interval_days} days
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#A08D8D]" />
                </div>
              ))}
              <div className="text-[11px] text-[#A08D8D] pt-6">
                All pause / skip / cancel / change-cadence actions happen securely in the Stripe billing portal above.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
